import dayjs from 'dayjs';
import { myVisitCounselApi } from '@/entities/planner/model/my-planner.api';
import { orderApi } from '@/entities/order';
import { isPlannerCounselHiddenByOrderStatus } from '@/entities/visit-counsel/model/planner-counsel-list-filters';
import type { VisitCounselView } from '@/entities/visit-counsel/model/visit-counsel.types';
import type { OrderStatus } from '@/entities/order/model/order.types';

function scheduleTs(c: VisitCounselView): number {
  return dayjs(`${c.scheduleDate} ${c.scheduleTime}`).valueOf();
}

const PAGE_SIZE = 100;
const MAX_PAGES = 10;

/**
 * 방금 종료한 상담 일시 이후, 미완료·목록 노출 가능한 다음 상담 1건
 */
export async function fetchNextPlannerCounselAfter(
  afterCounsel: VisitCounselView,
  plannerId: string
): Promise<VisitCounselView | null> {
  const afterTs = scheduleTs(afterCounsel);
  const items: VisitCounselView[] = [];
  let cursor: string | null | undefined = undefined;

  for (let p = 0; p < MAX_PAGES; p++) {
    const page = await myVisitCounselApi.searchMyVisitCounsels({
      filterGroups: [
        {
          logic: 'AND',
          filters: [{ field: 'counselorId', operator: 'EQUALS', value: plannerId, values: null }],
        },
      ],
      sortBy: 'scheduleDate',
      sortOrder: 'ASC',
      pageSize: PAGE_SIZE,
      cursor: cursor ?? undefined,
    });
    items.push(...page.items);
    if (!page.hasNext || page.nextCursor == null) break;
    cursor = page.nextCursor;
  }

  items.sort((a, b) => scheduleTs(a) - scheduleTs(b));

  const candidates = items.filter(
    (c) => c.id !== afterCounsel.id && !c.completedAt && scheduleTs(c) > afterTs
  );

  if (candidates.length === 0) return null;

  const uniqueOrderIds = [...new Set(candidates.map((c) => c.orderId))];
  const orderStatusMap = new Map<string, OrderStatus>();

  try {
    const orders = await orderApi.getOrdersByIds(uniqueOrderIds);
    for (const order of orders) {
      orderStatusMap.set(order.id, order.status);
    }
  } catch {
    return candidates[0] ?? null;
  }

  for (const c of candidates) {
    const status = orderStatusMap.get(c.orderId);
    if (!status) return c;
    if (!isPlannerCounselHiddenByOrderStatus(c, status)) return c;
  }

  return null;
}
